/**
 * Tells the analytics platforms who the visitor turned out to be.
 *
 * The anonymous half of every funnel is keyed on `kv_did` (see `device-id.ts`).
 * This is the other half: once someone signs in, GA gets a User-ID so its
 * reports can stitch that person's devices together, and Meta gets Advanced
 * Matching so a conversion can be attributed to the ad that produced it even
 * when third-party cookies are gone.
 *
 * Two hard rules, both about the email:
 *  - It NEVER reaches GA. Google's terms forbid personal data in event params,
 *    and a breach there is a property-suspension matter, not a warning.
 *  - It reaches Meta only as a SHA-256 hash computed in this browser. Meta's
 *    Pixel accepts either plain or normalized-and-hashed email for `em`, so
 *    hashing here costs nothing and means the plaintext is never written to
 *    storage or put on the wire.
 */
import { reportHandledException } from '@/lib/error-reporting'
import { gaMeasurementId } from '../analytics/config'

/** Just enough of the signed-in user to identify them to the platforms. */
export interface AnalyticsIdentityUser {
  id: number
  email: string
}

/**
 * Where the Pixel's Advanced Matching payload waits for the next page load.
 *
 * Meta's documentation is explicit that advanced matching values must be in the
 * pixel BASE CODE ("or the values will not be treated as manual advanced
 * matching values"). So the values are persisted here and read back by the base
 * snippet in `analytics-scripts.tsx` on the following load.
 *
 * Re-initialising the live pixel to identify it sooner was tried and rejected:
 * the library answers a second `fbq('init', …)` for an id it already holds with
 * `Duplicate Pixel ID`, and Meta warns that `track` on a page with multiple
 * initialised pixels "could produce over-firing or unexpected behavior" —
 * double-counted conversions cost far more than one page load of matching.
 */
export const PIXEL_ADVANCED_MATCHING_KEY = 'kiao-voice:pixel-advanced-matching'

/** The Advanced Matching fields we send. Both are Meta's own parameter names. */
export interface PixelAdvancedMatching {
  /** Lowercased, trimmed email, SHA-256 hashed in the browser. */
  em: string
  /** Our internal user id, which Meta expects in the clear. */
  external_id: string
}

/**
 * Orders concurrent identity writes.
 *
 * The caller's own guard cannot cover this: `syncAnalyticsIdentity` awaits the
 * hash, and the sign-out branch writes synchronously, so a sign-out that starts
 * DURING a sign-in's hash would be overwritten by it when the digest resolves —
 * handing the next page load the hashed address of someone who has already
 * signed out. On a shared browser that is the exact outcome
 * {@link syncAnalyticsIdentity} promises not to produce.
 */
let identitySequence = 0

type GtagFn = (...args: unknown[]) => void

function gtag(): GtagFn | null {
  if (typeof window === 'undefined') return null
  const fn = (window as { gtag?: GtagFn }).gtag
  return gaMeasurementId().length > 0 && typeof fn === 'function' ? fn : null
}

/**
 * Lowercase hex SHA-256 of the normalized email, or null when the browser
 * cannot hash.
 *
 * `crypto.subtle` only exists in a secure context, so plain-HTTP previews get
 * no `em`. Sending the plaintext instead is NOT the fallback: an unhashed
 * address is exactly what must never leave this function.
 */
export async function hashEmail(email: string): Promise<string | null> {
  const normalized = email.trim().toLowerCase()
  if (!normalized) return null
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined
  if (!subtle) return null
  try {
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(normalized))
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
  } catch (cause) {
    reportHandledException(cause, { source: 'lib/identity/analytics-identity.ts' })
    // Report + continue: identity resolution is an optimisation, and a browser
    // that refuses to hash must not break the page that asked.
    console.warn('[identity] could not hash the address for advanced matching:', cause)
    return null
  }
}

function readStoredMatching(): PixelAdvancedMatching | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(PIXEL_ADVANCED_MATCHING_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isPixelAdvancedMatching(parsed) ? parsed : null
  } catch (caughtError) {
    reportHandledException(caughtError, { source: 'lib/identity/analytics-identity.ts' })
    // Storage disabled or the value is corrupt — there is nothing to recover
    // and nothing that breaks; the visitor is simply matched less precisely.
    return null
  }
}

/**
 * Shape guard shared with the base snippet's inline copy. Both sides validate
 * because the value is read back out of storage another script could have
 * written, and it ends up inside a `fbq` call.
 */
export function isPixelAdvancedMatching(value: unknown): value is PixelAdvancedMatching {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.em === 'string' &&
    /^[0-9a-f]{64}$/.test(candidate.em) &&
    typeof candidate.external_id === 'string' &&
    /^[0-9]{1,20}$/.test(candidate.external_id)
  )
}

function writeStoredMatching(matching: PixelAdvancedMatching | null): void {
  if (typeof window === 'undefined') return
  try {
    if (matching) {
      window.localStorage.setItem(PIXEL_ADVANCED_MATCHING_KEY, JSON.stringify(matching))
    } else {
      window.localStorage.removeItem(PIXEL_ADVANCED_MATCHING_KEY)
    }
  } catch (cause) {
    reportHandledException(cause, { source: 'lib/identity/analytics-identity.ts' })
    console.warn('[identity] could not persist the advanced matching payload:', cause)
  }
}

/**
 * Report the current visitor to GA and Meta, or clear both on sign-out.
 *
 * Safe to call repeatedly and safe during SSR; GA is gated on being configured
 * and loaded, so an unconfigured or blocked install is a no-op rather than a
 * branch each caller has to write.
 *
 * GA is identified IMMEDIATELY; Meta at the NEXT page load, because advanced
 * matching belongs to the base snippet (see {@link PIXEL_ADVANCED_MATCHING_KEY}).
 * The same asymmetry applies to signing out: the GA User-ID is cleared here and
 * now, while the pixel already running on this page keeps the identity it was
 * initialised with — Meta offers no call to withdraw it — and only the next
 * load starts anonymous.
 */
export async function syncAnalyticsIdentity(user: AnalyticsIdentityUser | null): Promise<void> {
  const mine = ++identitySequence
  const ga = gtag()
  if (ga) {
    // `null`, not `undefined`: GA's `set` merges its argument, so an absent key
    // would leave a previous visitor's User-ID in place on a shared browser.
    ga('set', { user_id: user ? String(user.id) : null })
  }

  if (!user) {
    writeStoredMatching(null)
    return
  }

  const em = await hashEmail(user.email)
  // A later call already decided who this browser is; it owns the stored value
  // now, and writing either the hash OR a clear here would undo its decision.
  if (mine !== identitySequence) return

  if (!em) {
    // Clear rather than leave what is there. Anything stored belongs to whoever
    // signed in last, and on a shared browser that is a different person —
    // keeping it would identify the next page load as them.
    writeStoredMatching(null)
    return
  }

  writeStoredMatching({ em, external_id: String(user.id) })
}

/** The stored Advanced Matching payload, for the base snippet and its tests. */
export function currentPixelAdvancedMatching(): PixelAdvancedMatching | null {
  return readStoredMatching()
}
